// Generated by tutorials/record.cjs from tutorials/articles/h32-plan-of-care.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h32 --text-only` (wording) or
// `node tutorials/record.cjs h32` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'plan-of-care',
  catalogId: 'h32',
  title: 'Plan of care',
  summary: 'Write a resident\'s plan of care: pick the areas it covers, mark the needs, problems and interventions, say who is responsible, then keep it up to date and print it.',
  audience: 'Dietitians and dietary managers who write care plans',
  area: 'clinical',
  learn: [
    'Find the plans of care for your facility or for one resident',
    'Write a new plan of care, one area (domain) at a time',
    'Mark needs, problems, interventions and who is responsible',
    'Copy, complete and print a plan of care',
    'Where administrators set up the lists and report wording',
  ],
  keywords: [
    'plan of care',
    'care plan',
    'poc',
    'evaluation',
    'resident evaluation',
    'domain',
    'service need',
    'problem',
    'goal',
    'approach',
    'intervention',
    'responsible party',
    'level of care',
    'service plan',
    'iadl',
    'adl',
    'quick print',
    'completed',
    'new from existing',
    'review date',
    'follow up',
  ],
  steps: [
    {
      id: 'list',
      part: 'Find plans of care',
      heading: 'See every plan of care',
      text: 'Under Clinical, select Plan of Care. You see your facility\'s plans: the resident, the plan date, whether it is Active or Completed, the review date and who created it.\n\nSearch plans finds a resident or a diagnosis. Double-click a plan to open it.',
      media: {
        image: {
          src: '/help/plan-of-care/list.webp',
          width: 1280,
          height: 720,
        },
        alt: 'The Plan of Care list: seven plans, with Patient, POC Date, Status (Active or Completed), Review Date, Created By, Created and Diagnosis.',
      },
    },
    {
      id: 'resident',
      part: 'Find plans of care',
      heading: 'A resident\'s plans of care',
      text: 'Each resident\'s plans are also in their record: open the resident and select Plan of Care on the left. The newest is at the top, with who created it and, once it is done, who completed it.\n\nSelect a plan to open, complete or print it.',
      media: {
        image: {
          src: '/help/plan-of-care/resident.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/plan-of-care/resident.mp4',
          width: 1280,
          height: 720,
          durationSec: 6,
        },
        alt: 'Marjorie Ellison\'s Plan of Care section with New, Open, Completed and Reports, above two plans: one still open and one completed.',
      },
    },
    {
      id: 'domains',
      part: 'Write a new plan of care',
      heading: 'Select New, then the dates and areas',
      text: 'Select New. The first page sets the Plan of Care Date, the Initial Review Date and the Follow Up Date. Days sets the follow-up 30, 60 or 90 days after the plan date, or type your own number.\n\nThen check each area (domain) the plan covers, such as Nutrition and Hydration, and select Next. Each area you check gets its own page, in the order of the list; the arrows change the order.',
      media: {
        image: {
          src: '/help/plan-of-care/domains.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/plan-of-care/domains.mp4',
          width: 1280,
          height: 720,
          durationSec: 5.7,
        },
        alt: 'Create New Plan of Care for Marjorie Ellison: the Plan of Care, Initial Review and Follow Up dates, Days 90, and the domains Nutrition and Hydration checked, Dining not.',
      },
    },
    {
      id: 'needs',
      part: 'Write a new plan of care',
      heading: 'Check the resident\'s needs',
      text: 'Each area\'s page starts with its service needs. Check the ones that fit the resident. Each has a score; the total for the area shows at the bottom, and it cannot go over the area\'s maximum.\n\nIf some needs are marked with a warning, check at least one of them: the area needs a starting choice.',
      media: {
        image: {
          src: '/help/plan-of-care/needs.webp',
          width: 1280,
          height: 740,
        },
        clip: {
          src: '/help/plan-of-care/needs.mp4',
          width: 1280,
          height: 740,
          durationSec: 6,
        },
        alt: 'The Nutrition page: Needs cueing and encouragement to finish meals and Weigh weekly for {number} weeks, then monthly checked, and the footer Max Level of Care: 6, Total: 2.',
      },
    },
    {
      id: 'fill-in',
      part: 'Write a new plan of care',
      heading: 'Fill in the blanks',
      text: 'A need in curly brackets, such as {number}, has a blank to fill in. Click its wording; the bracketed part is selected, so just type over it and select Save.',
      media: {
        image: {
          src: '/help/plan-of-care/fill-in.webp',
          width: 942,
          height: 556,
        },
        clip: {
          src: '/help/plan-of-care/fill-in.mp4',
          width: 942,
          height: 556,
          durationSec: 2.4,
        },
        alt: 'The Modify Domain Need Text window: Long Description reads Weigh weekly for 4 weeks, then monthly.',
      },
    },
    {
      id: 'problems',
      part: 'Write a new plan of care',
      heading: 'Pick the problems and interventions',
      text: 'On the Problem and Needs / Interventions tab, check each problem the resident has. Then, with the problem selected, check what will be done about it on the right. Every problem you check needs at least one intervention.',
      media: {
        image: {
          src: '/help/plan-of-care/problems.webp',
          width: 1280,
          height: 740,
        },
        clip: {
          src: '/help/plan-of-care/problems.mp4',
          width: 1280,
          height: 740,
          durationSec: 5.7,
        },
        alt: 'Unplanned weight loss checked, with two of its interventions checked: report a loss of 2 lbs or more in a week to the dietitian, and offer fortified foods at meals.',
      },
    },
    {
      id: 'parties',
      part: 'Write a new plan of care',
      heading: 'Say who is responsible',
      text: 'On Responsible Parties, check who carries out this part of the plan. Some, such as Other, ask for a name or a word in Value.',
      media: {
        image: {
          src: '/help/plan-of-care/parties.webp',
          width: 1280,
          height: 742,
        },
        clip: {
          src: '/help/plan-of-care/parties.mp4',
          width: 1280,
          height: 742,
          durationSec: 6,
        },
        alt: 'The Responsible Parties tab with Registered Dietitian and Dietary Manager checked, and Other checked with Family typed in Value.',
      },
    },
    {
      id: 'next-area',
      part: 'Write a new plan of care',
      heading: 'Do the same for each area',
      text: 'Select Next for the next area and do the same: needs, problems and interventions, responsible parties. The steps across the top show where you are; Back returns to an earlier page.',
      media: {
        image: {
          src: '/help/plan-of-care/next-area.webp',
          width: 1280,
          height: 538,
        },
        clip: {
          src: '/help/plan-of-care/next-area.mp4',
          width: 1280,
          height: 538,
          durationSec: 6,
        },
        alt: 'The Hydration page: Needs fluids offered between meals checked, and Risk of dehydration checked with the intervention Offer 8 oz of fluid between meals.',
      },
    },
    {
      id: 'finish',
      part: 'Write a new plan of care',
      heading: 'Select Finish',
      text: 'On the last area, select Finish. If something is missing, a message says what, and the page shows the tab to fix.\n\nThe new plan is at the top of the resident\'s Plan of Care section. It stays Active until you mark it completed.',
      media: {
        image: {
          src: '/help/plan-of-care/finish.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/plan-of-care/finish.mp4',
          width: 1280,
          height: 720,
          durationSec: 3.1,
        },
        alt: 'Marjorie Ellison\'s Plan of Care section with today\'s plan at the top, created by Dana Morgan, and the message Plan of care saved.',
      },
    },
    {
      id: 'copy',
      part: 'Update, complete and print a plan',
      heading: 'Open, or start from a copy',
      text: 'Select a plan and Open (or double-click it) to change it. Areas already on the plan stay checked; you can add more.\n\nAt review time, select the latest plan, then the arrow next to New and New From Existing. Dietech copies the plan and opens the copy, so you only change what is different.',
      media: {
        image: {
          src: '/help/plan-of-care/copy.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/plan-of-care/copy.mp4',
          width: 1280,
          height: 720,
          durationSec: 3.8,
        },
        alt: 'Today\'s plan selected, with the New menu open: New... and New From Existing...',
      },
    },
    {
      id: 'completed',
      part: 'Update, complete and print a plan',
      heading: 'Mark it completed',
      text: 'When a plan is finished with, select it, then Completed and Completed... and select Yes. Dietech records who completed it and when. Not Completed... takes that back.',
      media: {
        image: {
          src: '/help/plan-of-care/completed.webp',
          width: 734,
          height: 300,
        },
        clip: {
          src: '/help/plan-of-care/completed.mp4',
          width: 734,
          height: 300,
          durationSec: 2,
        },
        alt: 'The Mark As Completed question: Are you sure you want to mark this evaluation as completed? with No and Yes.',
      },
    },
    {
      id: 'reports',
      part: 'Update, complete and print a plan',
      heading: 'Print the plan',
      text: 'Select the plan, then Reports. Quick Print prints the Service Plan, the Evaluation and LOCD (level of care) and the IADL/ADL sheet together; you can leave any of them out. Or pick one report on its own.\n\nFor the newest plan, the menu also offers the next few months, for a monthly IADL/ADL sheet.',
      media: {
        image: {
          src: '/help/plan-of-care/reports.webp',
          width: 1280,
          height: 720,
        },
        clip: {
          src: '/help/plan-of-care/reports.mp4',
          width: 1280,
          height: 720,
          durationSec: 2.4,
        },
        alt: 'The plan marked completed, and the Reports menu open: Quick Print, Quick Print for the coming months, Evaluation and LOCD, Service Plan and IADL/ADL.',
      },
    },
    {
      id: 'setup',
      part: 'Set up Plan of Care reports',
      heading: 'Plan of Care Setup',
      text: 'Under Clinical, Plan of Care Setup sets the wording the reports use: what the Service Plan says when a problem has no intervention, how many signature lines the IADL/ADL sheet has, and the scoring key printed on the Resident Evaluation. Select Save when you are done.',
      media: {
        image: {
          src: '/help/plan-of-care/setup.webp',
          width: 1280,
          height: 720,
        },
        alt: 'Plan of Care Setup: Report Service Plan No Intervention, IADL/ADL Signature Line Count 3, and the Resident Evaluation Scoring Key Text, with Cancel and Save.',
      },
    },
    {
      id: 'tables',
      part: 'Set up Plan of Care reports',
      heading: 'The areas, needs and interventions',
      text: 'The areas (domains) themselves, with their service needs, problems and interventions, are set up under Food Data > Tables, in POC Domains: open a domain to change its lists. POC Responsible Parties and POC Score Types are there too.',
      textOnly: true,
    },
  ],
  tips: [
    'Updating a plan at review time? Select the latest plan and New From Existing. You start from a copy, so you only change what is different.',
    'A need or intervention with words in curly brackets, such as {number}, has a blank to fill in. Click its wording and type over the bracketed part.',
    'Each page checks itself when you select Next or Finish: every problem you check needs at least one intervention, and the area needs at least one responsible party.',
  ],
  notes: [
    'The Schedule tab sets when a checked need happens (days, evenings or nights, a date or a repeating pattern) for the IADL/ADL sheet.',
    'Create Evaluation, on the first page of a new plan, saves it with nothing checked and prints the Resident Evaluation as a blank form to fill in by hand.',
    'Starting, copying and completing plans needs a Plan of Care Data Entry security level of 3 or more; deleting needs 4. The Reports menu needs a Plan of Care Report level. Ask your administrator if a button is missing.',
  ],
  related: ['nutrition-assessments', 'finding-a-resident', 'running-a-report', 'favorites-and-print-queue'],
  recorded: '2026-11-03',
};

export default article;
